import { loadLocalImage } from './image-loader.ts';
// #215 — Pin-to-element controller.
//
// Owns the pin-mode state machine, the fullscreen event-target overlay
// (which guarantees host page integrity by being the topmost element
// and catching all pointer/click events), the hover outline rendering,
// and the async capture path (selector + screenshot + bounding rect).
//
// The fullscreen overlay model is the key host-integrity guarantee:
// while pin mode is active, every pointer event lands on the overlay,
// never on host elements. We toggle `pointer-events: none` briefly to
// call `elementFromPoint` and find the underlying target.
//
// Cleanup is mandatory and idempotent — `dispose()` always removes the
// overlay even when `capture()` throws or times out. A leaked overlay
// blocks the customer's page until reload, so the failure path matters
// as much as the success path.
//
// See design: oncatch-wiki/wiki/decisions/2026-05-22-215-pin-to-element-design.md

import { generateSelector, type SelectorResult } from './pin-selector.ts';

export interface PinnedData {
  selector: string;
  selector_confidence: 'high' | 'medium' | 'low';
  selector_queryable: boolean;
  inner_selector: string | null;
  inner_selector_queryable: boolean;
  url: string;
  click: { x: number; y: number };
  rect: { x: number; y: number; width: number; height: number };
  viewport: { width: number; height: number; device_pixel_ratio: number };
  scroll: { x: number; y: number };
  screenshot: {
    natural_width: number;
    natural_height: number;
    capture_pixel_ratio: number;
  };
  inside_open_shadow: boolean;
  inside_closed_shadow: boolean;
}

export interface PinCaptureResult {
  pinned: PinnedData;
  screenshotBlob: Blob;
}

type PinState = 'idle' | 'pinning' | 'capturing' | 'captured';

interface PinControllerOptions {
  /** Async screenshot capture with an overlay highlight at the given rect. */
  captureScreenshot: (target: Element, rect: DOMRect) => Promise<Blob>;
  /** Called when a pin capture succeeds. */
  onCapture: (result: PinCaptureResult) => void;
  /** Called when pin mode cancels (Escape, navigation, dispose). */
  onCancel: () => void;
  /** Called when capture fails (timeout, screenshot error). */
  onCaptureError: (err: unknown) => void;
}

const CAPTURE_TIMEOUT_MS = 10_000;
const OVERLAY_Z_INDEX = 2_147_483_647; // max int32 — beats every host stacking context

export class PinController {
  private state: PinState = 'idle';
  private overlay: HTMLDivElement | null = null;
  private outline: HTMLDivElement | null = null;
  private listeners: Array<() => void> = [];
  private opts: PinControllerOptions;

  constructor(opts: PinControllerOptions) {
    this.opts = opts;
  }

  get currentState(): PinState {
    return this.state;
  }

  enter(): void {
    if (this.state !== 'idle') return; // idempotent
    this.state = 'pinning';
    this.mountOverlay();
    this.installListeners();
  }

  cancel(): void {
    if (this.state === 'idle') return;
    const wasCapturing = this.state === 'capturing';
    this.dispose();
    if (!wasCapturing) {
      this.opts.onCancel();
    }
  }

  /**
   * Always-safe cleanup. Called from cancel, capture finally, navigation,
   * widget destroy, etc. Idempotent.
   */
  dispose(): void {
    for (const off of this.listeners) {
      try {
        off();
      } catch {
        // ignore individual cleanup failures
      }
    }
    this.listeners = [];

    if (this.outline?.parentNode) this.outline.parentNode.removeChild(this.outline);
    this.outline = null;

    if (this.overlay?.parentNode) this.overlay.parentNode.removeChild(this.overlay);
    this.overlay = null;

    this.state = 'idle';
  }

  private mountOverlay(): void {
    const overlay = document.createElement('div');
    overlay.setAttribute('data-oncatch-pin-overlay', 'true');
    overlay.style.cssText = [
      'position: fixed',
      'inset: 0',
      `z-index: ${OVERLAY_Z_INDEX}`,
      'cursor: crosshair',
      'background: transparent',
      'pointer-events: auto',
    ].join(';');
    document.documentElement.appendChild(overlay);
    this.overlay = overlay;

    const outline = document.createElement('div');
    outline.setAttribute('data-oncatch-pin-outline', 'true');
    outline.style.cssText = [
      'position: fixed',
      'border: 2px solid #ff6b00',
      'background: rgba(255, 107, 0, 0.08)',
      `z-index: ${OVERLAY_Z_INDEX - 1}`,
      'pointer-events: none',
      'box-sizing: border-box',
      'transition: top 0.05s linear, left 0.05s linear, width 0.05s linear, height 0.05s linear',
      'display: none',
    ].join(';');
    document.documentElement.appendChild(outline);
    this.outline = outline;
  }

  private installListeners(): void {
    if (!this.overlay) return;
    const overlay = this.overlay;

    const onMove = (evt: PointerEvent) => {
      const target = this.targetUnderOverlay(evt.clientX, evt.clientY);
      if (!target || this.isExcluded(target)) {
        this.hideOutline();
        return;
      }
      this.positionOutline(target.getBoundingClientRect());
    };

    const onDown = (evt: PointerEvent) => {
      if (this.state !== 'pinning') return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
      const target = this.targetUnderOverlay(evt.clientX, evt.clientY);
      if (!target || this.isExcluded(target)) return;
      void this.capture(target, { x: Math.round(evt.clientX), y: Math.round(evt.clientY) });
    };

    const onKey = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') {
        evt.preventDefault();
        this.cancel();
      }
    };

    const onNavigate = () => this.cancel();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') this.cancel();
    };

    overlay.addEventListener('pointermove', onMove);
    overlay.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('popstate', onNavigate);
    document.addEventListener('visibilitychange', onVisibility);

    this.listeners.push(
      () => overlay.removeEventListener('pointermove', onMove),
      () => overlay.removeEventListener('pointerdown', onDown),
      () => document.removeEventListener('keydown', onKey, true),
      () => window.removeEventListener('popstate', onNavigate),
      () => document.removeEventListener('visibilitychange', onVisibility),
    );
  }

  private targetUnderOverlay(clientX: number, clientY: number): Element | null {
    if (!this.overlay) return null;
    // Briefly disable overlay's pointer-events so elementFromPoint
    // returns the element below it.
    this.overlay.style.pointerEvents = 'none';
    let target = document.elementFromPoint(clientX, clientY);
    this.overlay.style.pointerEvents = 'auto';

    // Recurse into open shadow roots
    let guard = 0;
    while (target && guard < 10) {
      const shadow = (target as Element & { shadowRoot: ShadowRoot | null }).shadowRoot;
      if (shadow?.mode !== 'open') break;
      const inner = shadow.elementFromPoint(clientX, clientY);
      if (!inner || inner === target) break;
      target = inner;
      guard += 1;
    }
    return target;
  }

  private isExcluded(target: Element): boolean {
    // Don't pin the html or body itself (the whole page is not a useful pin)
    if (target === document.documentElement || target === document.body) return true;
    // Don't pin elements we own (the overlay, outline, or anything inside the widget shadow host)
    if (target.hasAttribute('data-oncatch-pin-overlay')) return true;
    if (target.hasAttribute('data-oncatch-pin-outline')) return true;
    // Widget root host element identification — check ancestors for a known marker
    let cursor: Element | null = target;
    while (cursor) {
      if (cursor.tagName === 'ONCATCH-WIDGET' || cursor.id === 'oncatch-widget-root') {
        return true;
      }
      cursor = cursor.parentElement;
    }
    return false;
  }

  private positionOutline(rect: DOMRect): void {
    if (!this.outline) return;
    this.outline.style.display = 'block';
    this.outline.style.top = `${rect.top}px`;
    this.outline.style.left = `${rect.left}px`;
    this.outline.style.width = `${rect.width}px`;
    this.outline.style.height = `${rect.height}px`;
  }

  private hideOutline(): void {
    if (!this.outline) return;
    this.outline.style.display = 'none';
  }

  private async capture(target: Element, clickPt: { x: number; y: number }): Promise<void> {
    if (this.state !== 'pinning') return;
    this.state = 'capturing';
    // Hide the outline before capture so it doesn't appear in the screenshot
    this.hideOutline();

    const timeoutHandle = setTimeout(() => {
      // Fail-open: even if screenshot is stuck, restore the page.
      const err = new Error('Pin capture timed out after 10s');
      this.dispose();
      this.opts.onCaptureError(err);
    }, CAPTURE_TIMEOUT_MS);

    try {
      const selectorResult: SelectorResult = generateSelector(target);
      const rect = target.getBoundingClientRect();

      // Hide overlay during screenshot capture so it doesn't appear in the image
      if (this.overlay) this.overlay.style.display = 'none';
      const screenshotBlob = await this.opts.captureScreenshot(target, rect);
      // Guard: if dispose() ran while we were awaiting (timeout, cancel,
      // navigation), don't continue into onCapture. Without this guard a
      // slow screenshot can resolve AFTER the timeout's onCaptureError
      // fires, double-firing the lifecycle and stranding stale activePin.
      if (this.state !== 'capturing') return;
      if (this.overlay) this.overlay.style.display = 'block';

      const screenshotDims = await readImageDimensions(screenshotBlob);
      // Re-check after readImageDimensions await
      if (this.state !== 'capturing') return;
      const capturePixelRatio = Math.min(window.devicePixelRatio || 1, 2);

      const pinned: PinnedData = {
        selector: selectorResult.selector,
        selector_confidence: selectorResult.selector_confidence,
        selector_queryable: selectorResult.selector_queryable,
        inner_selector: selectorResult.inner_selector,
        inner_selector_queryable: selectorResult.inner_selector_queryable,
        url: window.location.href,
        click: clickPt,
        rect: {
          x: Math.round(rect.x),
          y: Math.round(rect.y),
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        },
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          device_pixel_ratio: window.devicePixelRatio || 1,
        },
        scroll: {
          x: Math.round(window.scrollX),
          y: Math.round(window.scrollY),
        },
        screenshot: {
          natural_width: screenshotDims.width,
          natural_height: screenshotDims.height,
          capture_pixel_ratio: capturePixelRatio,
        },
        inside_open_shadow: selectorResult.inside_open_shadow,
        inside_closed_shadow: selectorResult.inside_closed_shadow,
      };

      this.state = 'captured';
      this.opts.onCapture({ pinned, screenshotBlob });
    } catch (err) {
      this.opts.onCaptureError(err);
    } finally {
      clearTimeout(timeoutHandle);
      // ALWAYS clean up — overlay/outline removed regardless of success/error.
      this.dispose();
    }
  }
}

async function readImageDimensions(blob: Blob): Promise<{ width: number; height: number }> {
  const image = await loadLocalImage(blob);
  return { width: image.naturalWidth, height: image.naturalHeight };
}
